import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Copy, Download, RefreshCw } from 'lucide-react'
import { Button, Col, Container, Nav, Row } from 'react-bootstrap'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import Swal from 'sweetalert2'
import toast, { Toaster } from 'react-hot-toast'
import api from '../../../config/AxiosInstance'
import { transformKey } from '../../../helpers/constants'
import '../../../styles/style.css'
import * as Common from '../../../utils/Common'
import FilterSection from './components/FilterSection'
import ActionButtons from './components/ActionButtons'
import StockTable from './components/StockTable'

const headerTitleStyle = {
  color: 'var(--primary-orange)',
  fontSize: '24px',
  fontWeight: '600'
}

const MasterStock = () => {
  const { state } = useLocation()
  const navigate = useNavigate()
  const [warehouses, setWarehouses] = useState([])
  const [selectedWarehouse, setSelectedWarehouse] = useState(
    state?.warehouse_id || null
  )
  const [stocks, setStocks] = useState([])
  const [selectedID, setSelectedID] = useState([])
  const [selectedProblem, setSelectedProblem] = useState(null)
  const [itemName, setItemName] = useState(state?.item_name || '')
  const [debouncedItemName, setDebouncedItemName] = useState(itemName)
  const [source, setSource] = useState(state?.source || '')
  const [debouncedSource, setDebouncedSource] = useState(source)
  const [itemCode, setItemCode] = useState(state?.item_code || '')
  const [debouncedItemCode, setDebouncedItemCode] = useState(itemCode)
  const [batch, setBatch] = useState(state?.batch || '')
  const [debouncedBatch, setDebouncedBatch] = useState(batch)
  const [palletId, setPalletId] = useState(state?.pallet_id || '')
  const [debouncedPalletId, setDebouncedPalletId] = useState(palletId)
  const [stagingAreaId, setStagingAreaId] = useState('')
  const [debouncedStagingAreaId, setDebouncedStagingAreaId] = useState('')
  const [condition, setCondition] = useState('')
  const [debouncedCondition, setDebouncedCondition] = useState('')
  const [downloadingExport, setDownloadingExport] = useState(false)

  const [typeWarehouse, setTypeWarehouse] = useState(
    state?.typeWarehouse || 'internal'
  )
  const initialType =
    state?.type === 'quarantine'
      ? 'quarantine'
      : state?.type === 'reject' || state?.type === 'rejected'
        ? 'rejected'
        : 'normal'
  const [type, setType] = useState(initialType)

  const [loading, setLoading] = useState(false)
  const [meta, setMeta] = useState({
    per_page: 10,
    current_page: 1,
    total_row: 0,
    total_page: 0
  })
  const [downloadingRows, setDownloadingRows] = useState({})

  const refSrcWarehouse = useRef()

  const COLUMN_STOCK_NORMAL = [
    {
      key: 'item',
      label: 'Item',
      component: (data) => (
        <div className="fw-bold text-dark">
          {data.item_code || '-'} - {data.item_name || '-'}
        </div>
      )
    },
    { key: 'total_pack', label: 'Qty Kemasan' },
    { key: 'total_qty', label: 'Base Qty UOM' },
    { key: 'action', label: 'Action', className: 'text-end' }
  ]

  const COLUMN_STOCK_QUARANTINE_OR_REJECT = [
    { key: 'checkbox', label: 'Checkbox', width: '50px' },
    {
      key: 'item',
      label: 'Item',
      component: (data) => (
        <div className="fw-bold text-dark">
          {data.item_code || '-'} - {data.item_name || '-'}
        </div>
      )
    },
    {
      key: 'batch',
      label: 'Batch',
      component: (data) => (
        <div className="d-flex align-items-center gap-2">
          <span>{data.batch || '-'}</span>
          {data.batch && (
            <Copy
              size={14}
              className="text-muted cursor-pointer"
              style={{
                cursor: 'pointer',
                opacity: 0.5,
                transition: 'opacity 0.2s'
              }}
              onMouseEnter={(e) => (e.currentTarget.style.opacity = 1)}
              onMouseLeave={(e) => (e.currentTarget.style.opacity = 0.5)}
              onClick={() => handleCopy(data.batch)}
              title="Copy Batch"
            />
          )}
        </div>
      )
    },
    { key: 'total_pack', label: 'Qty Kemasan' },
    { key: 'total_qty', label: 'Base Qty UOM' },
    {
      key: 'location',
      label: 'Location',
      component: (data) => (
        <div className="d-flex align-items-center gap-2">
          <span>{data.location || '-'}</span>
          {data.location && (
            <Copy
              size={14}
              className="text-muted cursor-pointer"
              style={{
                cursor: 'pointer',
                opacity: 0.5,
                transition: 'opacity 0.2s'
              }}
              onMouseEnter={(e) => (e.currentTarget.style.opacity = 1)}
              onMouseLeave={(e) => (e.currentTarget.style.opacity = 0.5)}
              onClick={() => handleCopy(data.location)}
              title="Copy Location"
            />
          )}
        </div>
      )
    },
    {
      key: 'palet_id',
      label: 'Pallet ID',
      component: (data) => (
        <div className="d-flex align-items-center gap-2">
          <span>{data.palet_id || '-'}</span>
          {data.palet_id && (
            <Copy
              size={14}
              className="text-muted cursor-pointer"
              style={{
                cursor: 'pointer',
                opacity: 0.5,
                transition: 'opacity 0.2s'
              }}
              onMouseEnter={(e) => (e.currentTarget.style.opacity = 1)}
              onMouseLeave={(e) => (e.currentTarget.style.opacity = 0.5)}
              onClick={() => handleCopy(data.palet_id)}
              title="Copy Pallet ID"
            />
          )}
        </div>
      )
    },
    {
      key: 'item_problem',
      label: 'Problem',
      component: (data) =>
        data.item_problem ? transformKey(data.item_problem) : '-'
    },
    {
      key: 'item_reference',
      label: 'Related Ticket',
      component: (data) => {
        const url = getLinkReference(data)
        const content =
          url !== '' ? (
            <Link to={url} target="_blank" className="text-decoration-none">
              <span style={{ color: '#2F80ED' }}>{data.item_reference}</span>
            </Link>
          ) : (
            <span style={{ color: '#2F80ED' }}>{data.item_reference}</span>
          )

        return (
          <div className="d-flex align-items-center gap-2">
            {content}
            {data.item_reference && (
              <Copy
                size={14}
                className="text-muted cursor-pointer"
                style={{
                  cursor: 'pointer',
                  opacity: 0.5,
                  transition: 'opacity 0.2s'
                }}
                onMouseEnter={(e) => (e.currentTarget.style.opacity = 1)}
                onMouseLeave={(e) => (e.currentTarget.style.opacity = 0.5)}
                onClick={() => handleCopy(data.item_reference)}
                title="Copy Reference"
              />
            )}
          </div>
        )
      }
    },
    { key: 'action', label: 'Action', className: 'text-end' }
  ]

  const COLUMN_STOCK_VAS = [
    { key: 'checkbox', label: 'Checkbox', width: '50px' },
    {
      key: 'vas_source',
      label: 'VAS Source',
      component: (data) => (
        <div className="d-flex align-items-center gap-2">
          <span style={{ color: '#2F80ED' }}>{data.workorder || '-'}</span>
          {data.workorder && (
            <Copy
              size={14}
              className="text-muted cursor-pointer"
              style={{
                cursor: 'pointer',
                opacity: 0.5,
                transition: 'opacity 0.2s'
              }}
              onMouseEnter={(e) => (e.currentTarget.style.opacity = 1)}
              onMouseLeave={(e) => (e.currentTarget.style.opacity = 0.5)}
              onClick={() => handleCopy(data.workorder)}
              title="Copy VAS Source"
            />
          )}
        </div>
      )
    },
    {
      key: 'item',
      label: 'Item',
      component: (data) => (
        <div className="fw-bold text-dark">
          {data.item_code || '-'} - {data.item_name || '-'}
        </div>
      )
    },
    { key: 'item_alias', label: 'Item Alias' },
    {
      key: 'batch',
      label: 'Batch',
      component: (data) => (
        <div className="d-flex align-items-center gap-2">
          <span>{data.batch || '-'}</span>
          {data.batch && (
            <Copy
              size={14}
              className="text-muted cursor-pointer"
              style={{
                cursor: 'pointer',
                opacity: 0.5,
                transition: 'opacity 0.2s'
              }}
              onMouseEnter={(e) => (e.currentTarget.style.opacity = 1)}
              onMouseLeave={(e) => (e.currentTarget.style.opacity = 0.5)}
              onClick={() => handleCopy(data.batch)}
              title="Copy Batch"
            />
          )}
        </div>
      )
    },
    {
      key: 'palet_id',
      label: 'Pallet ID',
      component: (data) => (
        <div className="d-flex align-items-center gap-2">
          <span>{data.palet_id || '-'}</span>
          {data.palet_id && (
            <Copy
              size={14}
              className="text-muted cursor-pointer"
              style={{
                cursor: 'pointer',
                opacity: 0.5,
                transition: 'opacity 0.2s'
              }}
              onMouseEnter={(e) => (e.currentTarget.style.opacity = 1)}
              onMouseLeave={(e) => (e.currentTarget.style.opacity = 0.5)}
              onClick={() => handleCopy(data.palet_id)}
              title="Copy Pallet ID"
            />
          )}
        </div>
      )
    },
    {
      key: 'received_date',
      label: 'Received',
      component: (data) =>
        data.received_date
          ? new Date(data.received_date).toLocaleDateString('id-ID')
          : '-'
    },
    {
      key: 'qty_kemasan',
      label: 'Qty Kemasan',
      component: (data) => `${data.total_pack || 0} ${data.uom_pack || ''}`
    },
    {
      key: 'base_qty_uom',
      label: 'Base Qty UOM',
      component: (data) =>
        `${
          data.total_qty
            ? parseFloat(data.total_qty).toLocaleString('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              })
            : '0.00'
        } ${data.uom || ''}`
    }
  ]

  const COLUMN_STOCK_STAGING = [
    { key: 'checkbox', label: 'Checkbox', width: '50px' },
    { key: 'staging_area', label: 'Staging Area' },
    { key: 'palet_id', label: 'Pallet' },
    {
      key: 'item',
      label: 'Item',
      component: (data) => (
        <div className="fw-bold text-dark">
          {data.item_code || '-'} - {data.item_name || '-'}
        </div>
      )
    },
    { key: 'batch', label: 'Batch' },
    {
      key: 'qty_kemasan',
      label: 'Qty Kemasan',
      component: (data) => `${data.total_pack || 0} ${data.uom_pack || ''}`
    },
    {
      key: 'base_qty_uom',
      label: 'Base Qty UOM',
      component: (data) =>
        `${
          data.total_qty
            ? parseFloat(data.total_qty).toLocaleString('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              })
            : '0.00'
        } ${data.uom || ''}`
    },
    {
      key: 'condition',
      label: 'Condition',
      component: (data) => (
        <div
          className={`px-2 py-1 rounded-pill text-center text-white fw-bold`}
          style={{
            fontSize: '10px',
            backgroundColor:
              data.condition?.toLowerCase() === 'good' ? '#27AE60' : '#EB5757',
            width: 'fit-content'
          }}
        >
          {data.condition?.toLowerCase() === 'good' ? 'BAIK' : 'RUSAK'}
        </div>
      )
    },
    {
      key: 'source_ticket',
      label: 'Source Ticket',
      component: (data) => {
        let url = ''
        if (data.staging_type === 'inbound') {
          url = `/inbound/purchase-receipt/${data.source_id}`
        } else if (data.staging_type === 'outbound') {
          url = `/outbound/plan/detail/${data.source_id}`
        }

        return url !== '' ? (
          <Link to={url} target="_blank" className="text-decoration-none">
            <span style={{ color: '#2F80ED', cursor: 'pointer' }}>
              {data.source_ticket || '-'}
            </span>
          </Link>
        ) : (
          <span style={{ color: '#2F80ED', cursor: 'pointer' }}>
            {data.source_ticket || '-'}
          </span>
        )
      }
    }
  ]

  const [columns, setColumns] = useState(COLUMN_STOCK_NORMAL)

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text)
    toast.success('Copied to clipboard!', {
      position: 'top-right',
      style: {
        fontSize: '14px',
        fontWeight: '600'
      }
    })
  }

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedItemName(itemName)
      setDebouncedSource(source)
      setDebouncedItemCode(itemCode)
      setDebouncedBatch(batch)
      setDebouncedPalletId(palletId)
      setDebouncedStagingAreaId(stagingAreaId)
      setDebouncedCondition(condition)
      setMeta((prev) => ({ ...prev, current_page: 1 }))
    }, 1000)
    return () => clearTimeout(handler)
  }, [itemName, source, itemCode, batch, palletId, stagingAreaId, condition])

  useEffect(() => {
    const fetchWarehouse = async () => {
      try {
        const params = {
          is_parent: 1,
          type:
            typeWarehouse === 'external'
              ? 'external,consignment'
              : typeWarehouse,
          forUser: 1
        }
        const response = await api.get('/master/warehouses/all', { params })
        const options = [{ value: '', label: 'All Warehouses' }]
        response.data.data.forEach((w) => {
          options.push({
            value: w.id,
            label: w.warehouse_name,
            warehouse_id: w.warehouse_id
          })
        })
        setWarehouses(options)
      } catch (err) {
        const errMsg =
          err?.response?.data?.errors?.message || 'An error occurred'
        Common.showPopUpErrorMessage(errMsg)
      }
    }
    fetchWarehouse()
  }, [typeWarehouse])

  const fetchStock = useCallback(async () => {
    if (!selectedWarehouse) {
      setStocks([])
      return
    }

    try {
      setLoading(true)
      const params = {
        per_page: meta.per_page,
        page: meta.current_page,
        warehouse_id: selectedWarehouse,
        type: typeWarehouse,
        item_name: debouncedItemName,
        source: debouncedSource,
        item_code: debouncedItemCode,
        batch: debouncedBatch,
        pallet_id: debouncedPalletId
      }

      let url = 'master/stocks'
      if (type === 'quarantine') {
        url = 'master/stocks/karantina'
        params.problem = selectedProblem || ''
      } else if (type === 'rejected') {
        url = 'master/stocks/reject'
        params.problem = selectedProblem || ''
      } else if (type === 'vas') {
        url = 'master/stocks/vas'
      } else if (type === 'staging') {
        url = 'master/stocks/staging'
        params.staging_area_id = debouncedStagingAreaId
        params.condition = debouncedCondition
        params.source_ticket = debouncedSource
      }

      const response = await api.get(url, { params })
      setStocks(response.data.data)
      setMeta(response.data.metadata)
    } catch (err) {
      const errMsg = err?.response?.data?.errors?.message || 'An error occurred'
      Common.showPopUpErrorMessage(errMsg)
    } finally {
      setLoading(false)
    }
  }, [
    meta.per_page,
    meta.current_page,
    selectedWarehouse,
    typeWarehouse,
    debouncedItemName,
    debouncedSource,
    debouncedItemCode,
    debouncedBatch,
    debouncedPalletId,
    type,
    selectedProblem,
    debouncedStagingAreaId,
    debouncedCondition
  ])

  useEffect(() => {
    fetchStock()
  }, [fetchStock])

  useEffect(() => {
    if (type === 'quarantine' || type === 'rejected') {
      setColumns(COLUMN_STOCK_QUARANTINE_OR_REJECT)
    } else if (type === 'vas') {
      setColumns(COLUMN_STOCK_VAS)
    } else if (type === 'staging') {
      setColumns(COLUMN_STOCK_STAGING)
    } else {
      setColumns(COLUMN_STOCK_NORMAL)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type])

  const handleReset = () => {
    setSelectedWarehouse(null)
    setItemName('')
    setDebouncedItemName('')
    setSource('')
    setDebouncedSource('')
    setItemCode('')
    setDebouncedItemCode('')
    setBatch('')
    setDebouncedBatch('')
    setPalletId('')
    setDebouncedPalletId('')
    setStagingAreaId('')
    setDebouncedStagingAreaId('')
    setCondition('')
    setDebouncedCondition('')
    setSelectedProblem(null)
    setSelectedID([])
    setMeta((prev) => ({ ...prev, current_page: 1 }))
    refSrcWarehouse.current?.clearValue()
  }

  const handleSelectTypeWarehouse = (val) => {
    setTypeWarehouse(val)
    setSelectedWarehouse(null)
    setStocks([])
    setMeta((prev) => ({ ...prev, current_page: 1 }))
    refSrcWarehouse.current?.clearValue()
  }

  const handleSelectTypeStock = (val) => {
    setType(val)
    setMeta((prev) => ({ ...prev, current_page: 1 }))
    setSelectedID([])
    setSelectedProblem(null)
  }

  const handleExportVAS = async () => {
    if (!selectedWarehouse) {
      return Common.showPopUpErrorMessage('Please select a warehouse first')
    }
    try {
      setDownloadingExport(true)
      const params = {
        warehouse_id: selectedWarehouse,
        item_name: debouncedItemName,
        source: debouncedSource,
        item_code: debouncedItemCode,
        batch: debouncedBatch,
        pallet_id: debouncedPalletId
      }
      const response = await api.get('master/stocks/export-vas', {
        params,
        responseType: 'arraybuffer'
      })

      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      const warehouseName =
        warehouses.find((w) => w.value === selectedWarehouse)?.label ||
        selectedWarehouse
      link.setAttribute('download', `VAS_${warehouseName}.xlsx`)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (err) {
      const errMsg =
        err?.response?.data?.errors?.message ||
        'An error occurred during export'
      Common.showPopUpErrorMessage(errMsg)
    } finally {
      setDownloadingExport(false)
    }
  }

  const handleExportReject = async () => {
    if (!selectedWarehouse) {
      return Common.showPopUpErrorMessage('Please select a warehouse first')
    }
    try {
      setDownloadingExport(true)
      const params = {
        warehouse_id: selectedWarehouse,
        item_name: debouncedItemName,
        item_code: debouncedItemCode,
        batch: debouncedBatch,
        pallet_id: debouncedPalletId,
        problem: selectedProblem || ''
      }
      const response = await api.get('master/stocks/export-reject', {
        params,
        responseType: 'arraybuffer'
      })

      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      const warehouseName =
        warehouses.find((w) => w.value === selectedWarehouse)?.label ||
        selectedWarehouse
      link.setAttribute('download', `Rejected_${warehouseName}.xlsx`)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (err) {
      const errMsg =
        err?.response?.data?.errors?.message ||
        'An error occurred during export'
      Common.showPopUpErrorMessage(errMsg)
    } finally {
      setDownloadingExport(false)
    }
  }

  const handleExportStaging = async () => {
    if (!selectedWarehouse) {
      return Common.showPopUpErrorMessage('Please select a warehouse first')
    }
    try {
      setDownloadingExport(true)
      const params = {
        warehouse_id: selectedWarehouse,
        item_name: debouncedItemName,
        source_ticket: debouncedSource,
        item_code: debouncedItemCode,
        batch: debouncedBatch,
        pallet_id: debouncedPalletId,
        staging_area_id: debouncedStagingAreaId,
        condition: debouncedCondition
      }
      const response = await api.get('master/stocks/export-staging', {
        params,
        responseType: 'arraybuffer'
      })

      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      const warehouseName =
        warehouses.find((w) => w.value === selectedWarehouse)?.label ||
        selectedWarehouse
      link.setAttribute('download', `Staging_Area_${warehouseName}.xlsx`)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (err) {
      const errMsg =
        err?.response?.data?.errors?.message ||
        'An error occurred during export'
      Common.showPopUpErrorMessage(errMsg)
    } finally {
      setDownloadingExport(false)
    }
  }

  const handleDownloadLabel = async (slotItemId) => {
    try {
      setDownloadingRows((prev) => ({ ...prev, [slotItemId]: true }))
      const params = { print_label: 1 }
      let url = `master/stocks/karantina/${slotItemId}`
      if (type === 'rejected') {
        url = `master/stocks/reject/${slotItemId}`
      }
      const response = await api.get(url, {
        params,
        responseType: 'arraybuffer'
      })
      const { status, data } = response
      if (status === 200) {
        const blob = new Blob([data], { type: 'application/pdf' })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', `download-label-${slotItemId}.pdf`)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
      }
    } catch (err) {
      const errMsg = err?.response?.data?.errors?.message || 'An error occurred'
      Common.showPopUpErrorMessage(errMsg)
    } finally {
      setDownloadingRows((prev) => ({ ...prev, [slotItemId]: false }))
    }
  }

  const handleRedirectPageQuarantineReject = (slotItemId, targetType) => {
    navigate(`/master/stock/detail/${slotItemId}?type=${targetType}`, {
      state: {
        warehouse_id: selectedWarehouse,
        item_name: itemName,
        type: targetType,
        typeWarehouse
      }
    })
  }

  const handleDetail = (code) => {
    const encodedName = encodeURIComponent(code)
    navigate(`/master/stock/${encodedName}`, {
      state: {
        warehouse_id: selectedWarehouse,
        item_name: itemName,
        type,
        typeWarehouse
      }
    })
  }

  const getLinkReference = (data) => {
    if (data.item_reference_type === 'draft') {
      return `/inbound/purchase-receipt/${data.item_reference_id}`
    }
    return ''
  }

  const handleChangeCheckbox = (value = null) => {
    if (!value) {
      if (selectedID.length > 0) {
        setSelectedID([])
      } else {
        let selectableStocks = stocks
        if (type === 'vas') {
          selectableStocks = stocks.filter(
            (stock) => stock.workorder_type === 'repack'
          )
        } else if (type === 'staging') {
          selectableStocks = stocks.filter(
            (stock) => stock.staging_type !== 'inbound'
          )
        }

        if (type === 'quarantine' || type === 'rejected') {
          const firstProblem = selectableStocks[0]?.item_problem
          const allSame = selectableStocks.every(
            (stock) => stock.item_problem === firstProblem
          )
          if (!allSame) {
            return Common.showPopUpErrorMessage(
              'Please select the same problem label'
            )
          }
        }

        setSelectedID(
          selectableStocks.map((stock) => ({
            id: stock.slot_item_id || stock.id,
            problem: stock.item_problem
          }))
        )
      }
    } else {
      if (type === 'quarantine' || type === 'rejected') {
        const currentProblem = value.problem
        const hasDifferent = selectedID.some(
          (v) => v.problem !== currentProblem
        )
        if (hasDifferent) {
          return Common.showPopUpErrorMessage(
            'Please select the same problem label'
          )
        }
      }

      setSelectedID((prev) => {
        const exists = prev.find((item) => item.id === value.id)
        if (exists) return prev.filter((item) => item.id !== value.id)
        return [...prev, value]
      })
    }
  }

  const handleActionMutation = (targetType) => {
    if (!selectedID.length || !selectedWarehouse) {
      return Common.showPopUpErrorMessage(
        'Please select items and warehouse first'
      )
    }

    const validIDs = selectedID.map((item) => item.id).filter(Boolean)
    if (!validIDs.length) {
      return Common.showPopUpErrorMessage('No valid items selected')
    }

    navigate('/master/stock/send-to-quarantine', {
      state: {
        selectedIDs: validIDs,
        warehouse_id: selectedWarehouse,
        typeWarehouse,
        targetType,
        sourceType: type
      }
    })
  }

  const handleModalCreateTicket = (actionType) => {
    let title = ''
    let text = ''
    let url = ''

    if (actionType === 'transfer release') {
      text = 'Create Transfer to Warehouse (Restock)?'
      url = '/transfer/new-ticket'
    } else if (actionType === 'reject') {
      text = 'Create Transfer to Rejected Stock?'
      url = '/master/stocks/reject'
    }

    Swal.fire({
      title,
      text,
      icon: 'warning',
      iconColor: '#828282',
      showCancelButton: true,
      confirmButtonColor: '#f89d1d',
      confirmButtonText: 'Create',
      customClass: {
        title: 'swal-title-outbound',
        popup: 'swal-popup-outbound',
        actions: 'swal-cancel-pr'
      }
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const payload = {
            slot_items: selectedID.map((item) => item.id),
            type: typeWarehouse,
            warehouse_id: selectedWarehouse
          }
          const res = await api.post(url, payload)
          if (res.status === 200) {
            toast.success('Ticket created!', {
              position: 'top-right',
              style: {
                fontSize: '14px',
                fontWeight: '600'
              }
            })
            setSelectedID([])
            fetchStock()
          }
        } catch (err) {
          const errMsg =
            err?.response?.data?.errors?.message || 'An error occurred'
          Common.showPopUpErrorMessage(errMsg)
        }
      }
    })
  }

  const handleRedirectPageCreateTicket = (actionType) => {
    if (!['material_issue', 'disposal'].includes(actionType)) {
      return Common.showPopUpErrorMessage('Type is not allowed')
    }
    const stateToSend = {
      items: selectedID.map((item) => {
        const s = stocks?.find((stock) => stock.slot_item_id === item.id)
        if (s && actionType === 'material_issue') {
          s.bu = null
        }
        return s
      }),
      warehouse_id: selectedWarehouse,
      type: actionType,
      typeWarehouse
    }
    const pathHeader = actionType.replaceAll('_', '-')
    navigate(`/${pathHeader}/create`, { state: stateToSend })
  }

  const handlePageChange = (data) => {
    setMeta((prevMeta) => ({ ...prevMeta, current_page: data.selected + 1 }))
    window.scrollTo(0, 0)
  }

  const handleOnChangeMeta = (e) => {
    setMeta((prevMeta) => ({
      ...prevMeta,
      per_page: parseInt(e.target.value, 10),
      current_page: 1
    }))
  }

  const columnsMemo = useMemo(() => columns, [columns])

  return (
    <Container fluid className="px-4">
      <Row className="mb-4 align-items-center">
        <Col>
          <h3 style={headerTitleStyle}>Master Stock</h3>
        </Col>
        <Col className="d-flex justify-content-end">
          <Button
            variant="light"
            className="text-secondary fw-bold rounded px-3 border d-flex align-items-center justify-content-center gap-2"
            onClick={handleReset}
            style={{ fontSize: '0.85rem' }}
          >
            <RefreshCw size={16} />
            Reset Filter
          </Button>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col>
          <Nav
            variant="pills"
            activeKey={typeWarehouse}
            onSelect={handleSelectTypeWarehouse}
            className="bg-white rounded-pill p-1 d-inline-flex border"
            style={{ boxShadow: '0 2px 4px rgba(0,0,0,0.02)' }}
          >
            <Nav.Item>
              <Nav.Link
                eventKey="internal"
                className={`rounded px-4 ${
                  typeWarehouse === 'internal' ? 'text-white' : ''
                }`}
                style={{
                  cursor: 'pointer',
                  fontWeight: '600',
                  backgroundColor:
                    typeWarehouse === 'internal'
                      ? 'var(--primary-orange)'
                      : 'transparent',
                  color:
                    typeWarehouse === 'internal'
                      ? '#fff'
                      : 'var(--primary-orange)'
                }}
              >
                Internal
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link
                eventKey="external"
                className={`rounded px-4 ${
                  typeWarehouse === 'external' ? 'text-white' : ''
                }`}
                style={{
                  cursor: 'pointer',
                  fontWeight: '600',
                  backgroundColor:
                    typeWarehouse === 'external'
                      ? 'var(--primary-orange)'
                      : 'transparent',
                  color:
                    typeWarehouse === 'external'
                      ? '#fff'
                      : 'var(--primary-orange)'
                }}
              >
                External
              </Nav.Link>
            </Nav.Item>
          </Nav>
        </Col>
      </Row>

      <FilterSection
        type={type}
        warehouses={warehouses}
        selectedWarehouse={selectedWarehouse}
        setSelectedWarehouse={setSelectedWarehouse}
        refSrcWarehouse={refSrcWarehouse}
        setMeta={setMeta}
        source={source}
        setSource={setSource}
        itemCode={itemCode}
        setItemCode={setItemCode}
        itemName={itemName}
        setItemName={setItemName}
        batch={batch}
        setBatch={setBatch}
        palletId={palletId}
        setPalletId={setPalletId}
        stagingAreaId={stagingAreaId}
        setStagingAreaId={setStagingAreaId}
        condition={condition}
        setCondition={setCondition}
      />

      <div className="d-flex justify-content-between align-items-center mb-4 border-bottom">
        <Nav
          variant="underline"
          activeKey={type}
          onSelect={handleSelectTypeStock}
          className="border-0"
        >
          <Nav.Item>
            <Nav.Link
              eventKey="normal"
              className={`px-4 pb-3 ${
                type === 'normal'
                  ? 'text-primary-orange fw-bold border-bottom border-primary-orange border-3'
                  : 'text-secondary'
              }`}
              style={{ marginBottom: '-2px' }}
            >
              Normal
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link
              eventKey="quarantine"
              className={`px-4 pb-3 ${
                type === 'quarantine'
                  ? 'text-primary-orange fw-bold border-bottom border-primary-orange border-3'
                  : 'text-secondary'
              }`}
              style={{ marginBottom: '-2px' }}
            >
              Quarantine
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link
              eventKey="rejected"
              className={`px-4 pb-3 ${
                type === 'rejected'
                  ? 'text-primary-orange fw-bold border-bottom border-primary-orange border-3'
                  : 'text-secondary'
              }`}
              style={{ marginBottom: '-2px' }}
            >
              Rejected
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link
              eventKey="vas"
              className={`px-4 pb-3 ${
                type === 'vas'
                  ? 'text-primary-orange fw-bold border-bottom border-primary-orange border-3'
                  : 'text-secondary'
              }`}
              style={{ marginBottom: '-2px' }}
            >
              VAS
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link
              eventKey="staging"
              className={`px-4 pb-3 ${
                type === 'staging'
                  ? 'text-primary-orange fw-bold border-bottom border-primary-orange border-3'
                  : 'text-secondary'
              }`}
              style={{ marginBottom: '-2px' }}
            >
              Staging Area
            </Nav.Link>
          </Nav.Item>
        </Nav>

        {(type === 'vas' || type === 'staging') && (
          <div className="d-flex gap-2 pb-2">
            <Button
              variant="outline-warning"
              className="fw-bold d-flex align-items-center justify-content-center gap-2"
              onClick={
                type === 'staging' ? handleExportStaging : handleExportVAS
              }
              disabled={downloadingExport || !stocks?.length}
              style={{ borderRadius: '8px' }}
            >
              <Download size={18} />
              {downloadingExport ? 'Exporting...' : 'Export'}
            </Button>
            {(() => {
              const isDisabled =
                selectedID.length === 0 ||
                selectedID.some((item) => {
                  const stock = stocks?.find(
                    (s) => (s?.slot_item_id || s?.id) === item?.id
                  )
                  return ['waiting', 'damaged', 'coa_not_match'].includes(
                    stock?.condition
                  )
                })

              return (
                <Button
                  variant="secondary"
                  disabled={isDisabled}
                  onClick={() => handleActionMutation('restock')}
                  className="fw-bold text-white border-0"
                  style={{
                    borderRadius: '8px',
                    opacity: isDisabled ? 0.5 : 1,
                    backgroundColor: isDisabled ? '#adb5bd' : '#f89d1d'
                  }}
                >
                  Send Back
                </Button>
              )
            })()}
            <Button
              variant="secondary"
              disabled={selectedID.length === 0}
              onClick={() => handleActionMutation('karantina')}
              className="fw-bold text-white border-0"
              style={{
                borderRadius: '8px',
                opacity: selectedID.length === 0 ? 0.5 : 1,
                backgroundColor: selectedID.length === 0 ? '#adb5bd' : '#EB5757'
              }}
            >
              Quarantine Item
            </Button>
          </div>
        )}
      </div>

      <ActionButtons
        type={type}
        selectedProblem={selectedProblem}
        setSelectedProblem={setSelectedProblem}
        selectedID={selectedID}
        selectedWarehouse={selectedWarehouse}
        typeWarehouse={typeWarehouse}
        handleModalCreateTicket={handleModalCreateTicket}
        handleRedirectPageCreateTicket={handleRedirectPageCreateTicket}
        handleExportReject={handleExportReject}
        handleActionMutation={handleActionMutation}
        downloadingExport={downloadingExport}
        stocks={stocks}
      />

      <Row>
        <Col md={12}>
          <StockTable
            columns={columnsMemo}
            stocks={stocks}
            loading={loading}
            type={type}
            selectedID={selectedID}
            handleChangeCheckbox={handleChangeCheckbox}
            meta={meta}
            handlePageChange={handlePageChange}
            handleOnChangeMeta={handleOnChangeMeta}
            downloadingRows={downloadingRows}
            handleDownloadLabel={handleDownloadLabel}
            handleRedirectPageQuarantineReject={
              handleRedirectPageQuarantineReject
            }
            handleDetail={handleDetail}
          />
        </Col>
      </Row>

      <Toaster />
    </Container>
  )
}

export default MasterStock
